import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { chatListSessionsInfiniteOptions, chatListSharedWithMeOptions } from "@/api";

export type ChatsFilter = "all" | "starred" | "shared";

export interface ChatRowData {
  uuid: string;
  title?: string | null;
  updatedAt: string;
  isStarred: boolean;
  messageCount: number;
  /** The chat's space (own chats only). */
  spaceName?: string | null;
  spaceIsPersonal?: boolean;
  /** Shared-with-me rows: who shared it. */
  sharedBy?: string;
}

const PAGE_SIZE = 30;

/**
 * The chat history list (optionally only one space's chats, or only Build with AI chats). Own chats page from the
 * server (title search runs there); "Shared with me" is one unpaginated list, searched here.
 */
export function useChatHistory(
  filter: ChatsFilter,
  search: string,
  spaceUuid?: string,
  source: "user" | "build" = "user",
) {
  const own = useInfiniteQuery({
    ...chatListSessionsInfiniteOptions({
      query: {
        page_size: PAGE_SIZE,
        starred_only: filter === "starred",
        search: search || undefined,
        space_uuid: spaceUuid,
        ...(source === "build" ? { source } : {}),
      },
    }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
    enabled: filter !== "shared",
  });
  const shared = useQuery({
    ...chatListSharedWithMeOptions(),
    enabled: filter === "shared",
    select: (r) => r.data,
  });

  if (filter === "shared") {
    const term = search.toLowerCase();
    const rows: ChatRowData[] = (shared.data ?? [])
      .filter((s) => !term || (s.title ?? "").toLowerCase().includes(term))
      .map((s) => ({ ...s, sharedBy: s.sharing.sharedBy.name }));
    return { rows, isPending: shared.isPending, hasMore: false, loadMore: () => {}, loadingMore: false };
  }
  return {
    rows: (own.data?.pages ?? []).flatMap((p) => p.data) as ChatRowData[],
    isPending: own.isPending,
    hasMore: own.hasNextPage,
    loadMore: () => void own.fetchNextPage(),
    loadingMore: own.isFetchingNextPage,
  };
}
